import { useEffect, useLayoutEffect, useState, useCallback } from "react";
import { padTimeUnit } from "./utils";
import { toast } from "sonner";

const IS_SERVER = typeof window === "undefined";

export const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

export function useMediaQuery(
  query,
  { defaultValue = false, initializeWithValue = true } = {},
) {
  const getMatches = (query) => {
    if (IS_SERVER) {
      return defaultValue;
    }
    return window.matchMedia(query).matches;
  };

  const [matches, setMatches] = useState(() => {
    if (initializeWithValue) {
      return getMatches(query);
    }
    return defaultValue;
  });

  // Handles the change event of the media query.
  function handleChange() {
    setMatches(getMatches(query));
  }

  useIsomorphicLayoutEffect(() => {
    const matchMedia = window.matchMedia(query);

    // Triggered at the first client-side load and if query changes
    handleChange();

    // Use deprecated `addListener` and `removeListener` to support Safari < 14 (#135)
    if (matchMedia.addListener) {
      matchMedia.addListener(handleChange);
    } else {
      matchMedia.addEventListener("change", handleChange);
    }

    return () => {
      if (matchMedia.removeListener) {
        matchMedia.removeListener(handleChange);
      } else {
        matchMedia.removeEventListener("change", handleChange);
      }
    };
  }, [query]);

  return matches;
}

export const useTimer = (props) => {
  const [timeLeft, setTimeLeft] = useState({
    hari: "00",
    jam: "00",
    menit: "00",
    detik: "00",
  });

  const calculateTimeLeft = useCallback(() => {
    const total = Date.parse(new Date(props.deadline)) - Date.now();

    const days = Math.floor(total / (1000 * 60 * 60 * 24));
    const hours = Math.floor((total / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((total / (1000 * 60)) % 60);
    const seconds = Math.floor((total / 1000) % 60);

    return {
      hari: padTimeUnit(Math.max(0, days)),
      jam: padTimeUnit(Math.max(0, hours)),
      menit: padTimeUnit(Math.max(0, minutes)),
      detik: padTimeUnit(Math.max(0, seconds)),
    };
  }, [props.deadline]);

  useEffect(() => {
    setTimeLeft(calculateTimeLeft());

    const interval = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(interval);
  }, [calculateTimeLeft]);

  const timeIsOut =
    props.deadline !== null &&
    Object.values(timeLeft).every((time) => Number(time) === 0);

  return { timeLeft, timeIsOut };
};

export const useImageUploader = (formSetValue, maxFiles) => {
  const [selectedFilesData, setSelectedFilesData] = useState([]);

  useEffect(() => {
    return () => {
      selectedFilesData.forEach((data) => URL.revokeObjectURL(data.previewUrl));
    };
  }, [selectedFilesData]);

  const handleFileChange = useCallback(
    (event) => {
      const filesFromInput = event.target.files;
      if (filesFromInput && filesFromInput.length > 0) {
        const newFilesArray = Array.from(filesFromInput);

        if (
          maxFiles &&
          selectedFilesData.length + newFilesArray.length > maxFiles
        ) {
          toast.error(`Maksimal ${maxFiles} foto yang bisa diunggah.`);
          return;
        }

        const newFilesData = newFilesArray.map((file) => ({
          file,
          previewUrl: URL.createObjectURL(file),
        }));

        setSelectedFilesData((prevFilesData) => {
          const updated = [...prevFilesData, ...newFilesData];
          formSetValue(
            "file",
            updated.map((fd) => fd.file),
            { shouldValidate: true },
          );
          return updated;
        });
        toast.success(`Berhasil menambahkan ${newFilesArray.length} file!`);
      }
    },
    [selectedFilesData, formSetValue, maxFiles],
  );

  const handleRemoveImage = useCallback(
    (indexToRemove) => {
      setSelectedFilesData((prevFilesData) => {
        const fileToRemove = prevFilesData[indexToRemove];
        if (fileToRemove) {
          URL.revokeObjectURL(fileToRemove.previewUrl);
        }
        const updatedFilesData = prevFilesData.filter(
          (_, index) => index !== indexToRemove,
        );
        formSetValue(
          "file",
          updatedFilesData.map((fd) => fd.file),
          { shouldValidate: true },
        );
        toast.info("Foto berhasil dihapus.");
        return updatedFilesData;
      });
    },
    [formSetValue],
  );

  const resetFiles = useCallback(() => {
    selectedFilesData.forEach((data) => URL.revokeObjectURL(data.previewUrl));
    setSelectedFilesData([]);
    formSetValue("file", [], { shouldValidate: true });
  }, [selectedFilesData, formSetValue]);

  return {
    selectedFilesData,
    handleFileChange,
    handleRemoveImage,
    resetFiles,
  };
};
